import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { catchError, map, Observable, of, switchMap, throwError, timer } from 'rxjs';
import urlJoin from 'url-join';
import { ConnectionResponseDto, ConnectionStateEnum } from '../../login/services/dtos/connection-response';

@Injectable({
  providedIn: 'root',
})
export class ScaService {
  private urlOpenBanking = environment.urlOpenBanking;

  // POLLING => each 3 secs for 3 minutes
  private POLLING_INTERVAL = 3000;
  private POLLING_MAX_ATTEMPTS_COUNT = 60;

  constructor(private http: HttpClient) {}

  requestDecoupledResolution(input: {
    userId: string;
    userToken: string;
    connectionId: string;
  }): Observable<ConnectionResponseDto> {
    const { userId, userToken, connectionId } = input;

    const requestDecoupledResolutionUrl = urlJoin(this.urlOpenBanking, 'auth', 'sca');

    return this.http.post<ConnectionResponseDto>(
      requestDecoupledResolutionUrl,
      {
        connectionId: connectionId,
        userId: userId,
      },
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  pollUntilDecoupledIsResolved(input: {
    userId: string;
    userToken: string;
    connectionId: string;
    retryCount?: number;
  }): Observable<unknown> {
    const { userId, userToken, connectionId } = input;

    const retryCount = input.retryCount ?? 0;

    return this.getConnectionState({
      userId,
      userToken,
      connectionId,
    }).pipe(
      switchMap((state) => {
        if (state === ConnectionStateEnum.VALID) {
          return of(state);
        } else if (state === ConnectionStateEnum.DECOUPLED || state === ConnectionStateEnum.VALIDATING) {
          if (retryCount < this.POLLING_MAX_ATTEMPTS_COUNT) {
            return timer(this.POLLING_INTERVAL).pipe(
              switchMap(() =>
                this.pollUntilDecoupledIsResolved({
                  userId,
                  userToken,
                  connectionId,
                  retryCount: retryCount + 1,
                })
              )
            );
          } else {
            return throwError(() => 'MAX_RETRIES_REACHED');
          }
        } else {
          return throwError(() => `Unexpected state received ${state}`);
        }
      })
    );
  }

  getConnectionState(input: {
    userToken: string;
    userId: string;
    connectionId: string;
  }): Observable<ConnectionStateEnum> {
    const { userId, userToken, connectionId } = input;

    const getConnectionUrl = urlJoin(this.urlOpenBanking, 'users', userId, 'connections', connectionId);

    return this.http
      .get<ConnectionResponseDto>(getConnectionUrl, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      })
      .pipe(
        map((res) => {
          return res.state;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
}
